/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Coding,
  Consent,
  Extension,
  HealthcareService,
  Practitioner,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
  ServiceRequest
} from '@medplum/fhirtypes'
import dayjs, { Dayjs, duration } from 'dayjs'
import {
  BaseAppointmentFragment,
  BaseQuestionnaireResponseFragment,
  Get_Appointments_By_TypeQuery
} from 'generated/graphql'
import { QUESTIONNAIRE_COMPONENT_EXTENSION_URL } from 'fhir/Questionnaire/constants'
import { AssessmentType } from 'components/Clinical/Assessments/constants'
import { CERTIFICATION_PERIOD_DAYS } from 'utils/constants'
import { LOINC_CODE_PRIOR_AUTH_REQUEST } from 'fhir/ServiceRequest/categories'
import { SERVICE_REQUEST_SERVICE_TYPE_EXTENSION } from 'fhir/ServiceRequest/constants'
import { DisplayableAuthorizationHours } from 'fhir/Consent/helpers'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { checkFeatureConfigByServiceTypes } from 'utils/featureConfig'
import { CustomFeatureConfigKey } from 'utils/featureConfig/constants'
import { HL7_TERMINOLOGY_CODES_LICENSES, LICENSE_OPTION_CODES } from 'fhir/constants'
import { getErrorMessage } from 'utils/helpers'
import { NotifyType } from 'contexts/NotificationContext'
import { sanitizeInput } from 'utils/inputSanitization'
import { isCodingObject } from 'fhir/utils'
import { QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE } from './questionnaire'
import { NUMBER_QUESTIONNAIRE_ITEM_TYPES, QuestionnaireAnswerType } from './types'
import {
  AllQuestionnaireItemType,
  InterventionInfo,
  SharedCareQuestionnaireItem
} from './hooks/constants'
import { AssessmentGenerationError } from './constants'
import { SelectOption } from './components/types'

const DAYS_PER_WEEK = 7

export const findExtensionAndReturnComponentRef = ({ extensions, linkId }): string => {
  const extension = extensions.find((ext) => {
    if (ext?.url && ext?.valueCodeableConcept) {
      return ext.url === QUESTIONNAIRE_COMPONENT_EXTENSION_URL && ext.valueCodeableConcept?.coding
    }
  })

  if (extension) {
    const elementRef = (extension.valueCodeableConcept?.coding?.[0]?.code || '') as string
    return elementRef
  }

  throw new Error(`Missing element type for item with linkId: ${linkId}`)
}

export const findComponentExtensionAndReturnDisplay = (extensions: Extension[]): string => {
  const extension = extensions.find((ext) => {
    if (ext?.url && ext?.valueCodeableConcept) {
      return ext.url === QUESTIONNAIRE_COMPONENT_EXTENSION_URL && ext.valueCodeableConcept?.coding
    }
  })
  if (extension) {
    return extension.valueCodeableConcept?.coding?.[0]?.display || ''
  }
  return ''
}

export const updateObjectByLinkId = (
  items: QuestionnaireResponseItem[],
  targetLinkId: string | undefined,
  newValue: QuestionnaireResponseItem
): QuestionnaireResponseItem[] => {
  return items.map((item) => {
    if (item.linkId === targetLinkId) {
      return {
        ...item,
        answer: newValue.answer
      }
    }
    if (item.item && Array.isArray(item.item)) {
      return { ...item, item: updateObjectByLinkId(item.item, targetLinkId, newValue) }
    }

    return item
  })
}

export const filterKeysWithSubstrings = <T>(
  inputObject: Record<string, T>,
  substrings: string[]
): Record<string, T> => {
  return Object.fromEntries(
    Object.entries(inputObject).filter(([key]) =>
      substrings.every((substring) => key.includes(substring))
    )
  )
}

export const extractAnswers = (
  questionnaireResponse: QuestionnaireResponse | QuestionnaireResponseItem | undefined
): Record<string, QuestionnaireResponseItemAnswer> => {
  const answersMap = {}

  const processItems = (items: QuestionnaireResponseItem[]): void => {
    items.forEach(({ linkId, answer, item: nestedItems }) => {
      if (answer) {
        answersMap[linkId as string] = answer.length === 1 ? answer[0] : answer
      }
      if (nestedItems) {
        processItems(nestedItems)
      }
    })
  }

  if (questionnaireResponse?.item) {
    processItems(questionnaireResponse.item)
  }

  return answersMap
}

export const formatDiagnosesList = (
  answers: Record<string, QuestionnaireResponseItemAnswer>
): string[] =>
  Object.values(answers).flatMap((answer) =>
    (Array.isArray(answer) ? answer : [answer])
      .map((item) => item?.valueCoding?.display)
      .filter(Boolean)
  )

export const getNestedItems = (
  items: QuestionnaireItem[]
): {
  question: QuestionnaireItem
  notes: QuestionnaireItem
  conditionalQuestions: QuestionnaireItem[]
} => {
  const questionGroupItems = {
    question: {} as QuestionnaireItem,
    notes: {} as QuestionnaireItem,
    conditionalQuestions: [] as QuestionnaireItem[]
  }
  items.forEach((item) => {
    const splitPath = item?.linkId?.split('/')
    if (splitPath && splitPath[splitPath.length - 1] === 'question') {
      questionGroupItems.question = item
    }
    if (splitPath && splitPath[splitPath.length - 1] === 'notes') {
      questionGroupItems.notes = item
    }
    if (splitPath && splitPath[splitPath.length - 1].includes('question-')) {
      questionGroupItems.conditionalQuestions.push(item)
    }
  })
  return questionGroupItems
}

export const getValueKeyForAnswerType = (answerType: QuestionnaireAnswerType): string => {
  switch (answerType) {
    case QuestionnaireAnswerType.STRING:
      return 'valueString'
    case QuestionnaireAnswerType.BOOLEAN:
      return 'valueBoolean'
    case QuestionnaireAnswerType.DATE:
      return 'valueDate'
    case QuestionnaireAnswerType.DATE_TIME:
      return 'valueString'
    // we need to use valueString because other wise it wouldn't save on Medplum
    // https://github.com/abbycare/abbycare/pull/699/files#r1428555435
    case QuestionnaireAnswerType.TIME:
      return 'valueString'
    case QuestionnaireAnswerType.DECIMAL:
      return 'valueDecimal'
    case QuestionnaireAnswerType.INTEGER:
      return 'valueInteger'
    case QuestionnaireAnswerType.REFERENCE:
      return 'valueReference'
    case QuestionnaireAnswerType.CODING:
      return 'valueCoding'
    default:
      throw new Error(`Cannot get value for answer type ${answerType}`)
  }
}

export const encodeValueForAnswerType = (
  value: any,
  answerType: QuestionnaireAnswerType
): boolean | number | string | Coding | Reference | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  // TODO: have to save just the timestamp + timezone portion instead of
  // the entire datetime
  switch (answerType) {
    case QuestionnaireAnswerType.DATE_TIME:
    case QuestionnaireAnswerType.TIME:
      return dayjs(value).format()
    case QuestionnaireAnswerType.DATE:
      return dayjs(value).format('YYYY-MM-DD')
    case QuestionnaireAnswerType.DECIMAL:
      return parseFloat(value)
    case QuestionnaireAnswerType.BOOLEAN:
      return Boolean(value)
    case QuestionnaireAnswerType.STRING:
      return sanitizeInput(value as string)
    default:
      return value
  }
}

export const decodeValueForAnswerType = (value: any, answerType: QuestionnaireAnswerType): any => {
  if (value === undefined || value === null) return value

  // TODO: check if dates have to make sense for times in range
  // e.g. 4pm to 9am, does second date have to be after first date
  switch (answerType) {
    case QuestionnaireAnswerType.DATE_TIME:
    case QuestionnaireAnswerType.DATE:
    case QuestionnaireAnswerType.TIME:
      return dayjs(value)
    default:
      return value
  }
}

/**
 * Get the number answer from a questionnaire response item
 * @param questionnaireResponseItem - The questionnaire response item
 * @returns The number answer
 */
export const getNumberAnswerFromItem = (
  questionnaireResponseItem?: QuestionnaireResponseItem
): number | undefined => {
  if (!questionnaireResponseItem) {
    return undefined
  }
  const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[questionnaireResponseItem.linkId]
  if (!NUMBER_QUESTIONNAIRE_ITEM_TYPES.includes(answerType)) {
    throw new Error(`Answer type ${answerType} is not a number`)
  }
  const answers = questionnaireResponseItem.answer || []
  const valueKey = getValueKeyForAnswerType(answerType)
  const answerValues = answers.flatMap((a) => a[valueKey])

  if (answerValues.length > 1) {
    throw new Error(`Expected 1 answer value, got ${answerValues.length}`)
  }
  const value = answerValues?.at(0)

  if (!value) {
    return undefined
  }

  return isNaN(parseInt(value)) ? undefined : Number(value)
}

/**
 * Get the text answer from a questionnaire response item
 * @param questionnaireResponseItem - The questionnaire response item
 * @returns The text answer
 */
export const getTextAnswerFromItem = (
  questionnaireResponseItem?: QuestionnaireResponseItem
): string | undefined => {
  if (!questionnaireResponseItem) {
    return undefined
  }
  const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[questionnaireResponseItem.linkId]

  if (answerType !== QuestionnaireAnswerType.STRING) {
    throw new Error(`Answer type ${answerType} is not a string`)
  }
  const answers = questionnaireResponseItem.answer || []
  const values = answers.flatMap((a) => a.valueString)

  if (values.length > 1) {
    throw new Error(`Expected 1 answer value, got ${values.length}`)
  }

  return values.at(0)
}

/**
 * Get the date answer from a questionnaire response item
 * @param questionnaireResponseItem - The questionnaire response item
 * @param parse - Whether to parse the date
 * @returns The date answer
 */
export const getDateAnswerFromItem = (
  questionnaireResponseItem?: QuestionnaireResponseItem,
  parse = false
): string | Dayjs | undefined => {
  if (!questionnaireResponseItem) {
    return undefined
  }

  const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[questionnaireResponseItem.linkId]
  if (![QuestionnaireAnswerType.DATE_TIME, QuestionnaireAnswerType.DATE].includes(answerType)) {
    throw new Error(`Answer type ${answerType} is not a date`)
  }

  const value =
    answerType === QuestionnaireAnswerType.DATE_TIME
      ? questionnaireResponseItem.answer?.at(0)?.valueDateTime
      : questionnaireResponseItem.answer?.at(0)?.valueDate

  if (!value) {
    return undefined
  }

  return parse ? dayjs(value) : value
}

/**
 * Get the reference answer from a questionnaire response item
 * @param questionnaireResponseItem - The questionnaire response item
 * @returns The reference answer
 */
export const getReferenceAnswerFromItem = (
  questionnaireResponseItem?: QuestionnaireResponseItem
): string | undefined => {
  if (!questionnaireResponseItem) {
    return undefined
  }
  const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[questionnaireResponseItem.linkId]
  if (answerType !== QuestionnaireAnswerType.REFERENCE) {
    throw new Error(`Answer type ${answerType} is not a reference`)
  }
  return questionnaireResponseItem.answer?.at(0)?.valueReference?.reference
}

export const getAnswerValuesFromItems = (
  questionnaireResponseItems: QuestionnaireResponseItem[],
  linkId: AllQuestionnaireItemType,
  rawAnswer = false
): any[] => {
  const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[linkId]
  if (answerType && answerType !== QuestionnaireAnswerType.GROUP) {
    const answer = questionnaireResponseItems?.find((i) => i?.linkId === linkId)?.answer ?? []
    const valueKey = getValueKeyForAnswerType(answerType)
    // NOTE: this should allow returning null or undefined, since we might actually
    // want that information to be returned (e.g. when adding new goals via the UI)
    const answerValues = answer.flatMap((a) => a[valueKey])
    return answerValues.map((value) =>
      rawAnswer ? value : decodeValueForAnswerType(value, answerType)
    )
  } else if (answerType == QuestionnaireAnswerType.GROUP) {
    return questionnaireResponseItems?.find((i) => i.linkId === linkId)?.answer ?? []
  }
  return []
}

export const createAnswerItemsFromValues = (
  questionnaireItemsWithValues: [AllQuestionnaireItemType, unknown][]
): QuestionnaireResponseItem[] => {
  return questionnaireItemsWithValues.map(([questionnaireItem, value]) => {
    const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[questionnaireItem]

    if (!answerType)
      throw new Error(`Could not find answer type for questionnaire item ${questionnaireItem}`)

    const valueAsArray =
      value === null || value === undefined ? [] : Array.isArray(value) ? value : [value]
    return {
      linkId: questionnaireItem,
      answer: valueAsArray.map((v) => ({
        [getValueKeyForAnswerType(answerType)]: encodeValueForAnswerType(v, answerType)
      }))
    }
  })
}

/**
 * Mutates existing flat answer items with new values, preserving other properties
 * Similar to onValueChange but for adapters only
 *
 * @param flatAnswers - Current flat answers record to mutate
 * @param linkIdsWithValues - Array of linkId and value pairs to update
 * @returns The mutated flat answers record
 */
export const mutateFlatAnswers = (
  flatAnswers: Record<string, QuestionnaireResponseItem>,
  linkIdsWithValues: Array<{ linkId: string; value: unknown }>
): Record<string, QuestionnaireResponseItem> => {
  for (const { linkId, value } of linkIdsWithValues) {
    const answerType = QUESTIONNAIRE_ITEM_TO_ANSWER_TYPE[linkId]
    if (answerType === undefined) {
      throw new Error(`Could not find answer type for questionnaire item ${linkId}`)
    }
    // Get or create the answer item
    if (!flatAnswers[linkId]) {
      flatAnswers[linkId] = { linkId }
    }
    const answerItem = flatAnswers[linkId]
    const valueAsArray = Array.isArray(value) ? value : [value]

    if (answerType === QuestionnaireAnswerType.GROUP) {
      // These are groups of multiple values which need to be handled differently. As a result, we
      // should assume that they have the structure of a QuestionnaireResponseItemAnswer, and
      // directly write to the answer array.
      answerItem.answer = valueAsArray
    } else {
      // These are actual values scoped to this specific link ID, and are not groups of multiple
      // values which need to be handled differently. As a result, we need to transform them to
      // the right QuestionnaireResponseItemAnswer structure by ensuring they're written
      // to the right valueX field + etc., and then save.
      answerItem.answer = valueAsArray.map((v) => ({
        [getValueKeyForAnswerType(answerType)]: encodeValueForAnswerType(v, answerType)
      }))
    }

    // Since an item cannot have both an item and an answer, remove the item if we add answers
    answerItem.item = undefined
  }

  return flatAnswers
}

export const getValueFromEvent = (e: unknown): any => {
  if (e && typeof e === 'object' && 'target' in e) {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    if (target && 'value' in target) {
      return target.value
    }
  } else {
    return e
  }
}

export const getSocAppointmentStartDate = (
  socAppointmentData: Get_Appointments_By_TypeQuery
): Dayjs | undefined => {
  const appointmentList =
    socAppointmentData.AppointmentList?.filter((a): a is BaseAppointmentFragment => !!a) ?? []
  const latestAppointment = appointmentList.find((a) => !!a)
  if (!latestAppointment) {
    return
  }

  const appointmentStartString = latestAppointment.start
  const appointmentDate = appointmentStartString ? dayjs(appointmentStartString) : undefined

  return appointmentDate
}

export const composeInterventionInstruction = (
  rawIntervention: string,
  intervention = '',
  type = '',
  indication = '',
  frequency = ''
): string => {
  const instruction = rawIntervention
    .replace('(Intervention/task)', intervention)
    .replace('(Intervention/task dropdown)', intervention)
    .replace('(type)', type)
    .replace('(type dropdown)', type)
    .replace('(indication)', indication)
    .replace('(indication dropdown)', indication)
    .replace('(frequency)', frequency)
    .replace('(Frequency)', frequency)

  return instruction
}

export const getInterventionFrequency = (frequency: string[] | undefined): string => {
  if (!frequency) return ''
  if (frequency.length === 0) {
    return 'Daily'
  }
  return frequency.join('s,') + 's'
}

export const getInterventionVisits = (visitNumbers: number[] | undefined): string => {
  if (!visitNumbers) return ''
  if (visitNumbers.length === 0) {
    return 'All'
  }

  return visitNumbers.join(',')
}

export type InterventionInfoWithDuration = {
  interventions: InterventionInfo[]
  duration: string
}
export const mapCustomVisitsToInterventions = (
  flatAnswers: Record<string, QuestionnaireResponseItem>,
  interventions: InterventionInfo[]
): Record<string, InterventionInfoWithDuration> => {
  const customVisits =
    flatAnswers[SharedCareQuestionnaireItem('VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS')]?.answer ??
    []

  const visitNumberMap = customVisits.reduce(
    (acc, visit, index): Record<string, InterventionInfoWithDuration> => {
      const visitNumber = index + 1

      const visitPeriod =
        visit.item?.find(
          (item) =>
            item.linkId ===
            SharedCareQuestionnaireItem('VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD')
        )?.item ?? []

      const [[start], [end]] = [
        SharedCareQuestionnaireItem('VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_START'),
        SharedCareQuestionnaireItem('VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_END')
      ].map((linkId) => getAnswerValuesFromItems(visitPeriod, linkId))

      const hoursDifference = end.diff(start, 'hour', true)
      const pluralSuffix = hoursDifference === 1 ? '' : 's'
      const duration = `${hoursDifference.toFixed(1)} hour${pluralSuffix}`

      acc[visitNumber] = { duration, interventions: [] }
      return acc
    },
    {} as Record<string, InterventionInfoWithDuration>
  )

  interventions.forEach((intervention) => {
    if (intervention?.visitNumbers && intervention.visitNumbers.length > 0) {
      intervention.visitNumbers.forEach((visitNumber) => {
        if (visitNumberMap[visitNumber]) {
          visitNumberMap[visitNumber].interventions.push(intervention)
        }
      })
    } else {
      Object.values(visitNumberMap).forEach((visitObject) => {
        visitObject.interventions.push(intervention)
      })
    }
  })

  return visitNumberMap
}

export const formatTreatmentFrequencyForCertPeriod = (
  displayableAuthorizationHours: DisplayableAuthorizationHours
): string => {
  const formatPluralString = (word: string, value: number): string =>
    value !== 1 ? `${word}s` : word

  if (displayableAuthorizationHours.unit === 'daily') {
    const treatmentFrequency = `${displayableAuthorizationHours.hours} ${formatPluralString(
      'hour',
      displayableAuthorizationHours.hours
    )} per day, ${DAYS_PER_WEEK} ${formatPluralString(
      'day',
      DAYS_PER_WEEK
    )} per week, for ${CERTIFICATION_PERIOD_DAYS} ${formatPluralString(
      'day',
      CERTIFICATION_PERIOD_DAYS
    )}`

    return treatmentFrequency
  } else if (displayableAuthorizationHours.unit === 'weekly') {
    const treatmentFrequency = `${displayableAuthorizationHours.hours} ${formatPluralString(
      'hour',
      displayableAuthorizationHours.hours
    )} per week, for ${CERTIFICATION_PERIOD_DAYS} ${formatPluralString(
      'day',
      CERTIFICATION_PERIOD_DAYS
    )}`

    return treatmentFrequency
  } else {
    throw new Error(`Invalid authorization hours unit: ${displayableAuthorizationHours.unit}`)
  }
}

export const formatTreatmentFrequencyString = (
  displayableAuthorizationHours: DisplayableAuthorizationHours,
  serviceTypes: ServiceTypeName[]
): string | null => {
  if (!serviceTypes.length || displayableAuthorizationHours.hours === 0) {
    return null
  }
  const hasStandardizedHours = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.HasStandardizedHours,
    serviceTypes ?? undefined
  )
  if (!hasStandardizedHours) {
    return null
  }
  return `Frequency Aide: ${formatTreatmentFrequencyForCertPeriod(displayableAuthorizationHours)}`
}

export const getAssessmentDisplay = (
  assessment: BaseQuestionnaireResponseFragment,
  qualifiedHours: number | undefined
): string => {
  const qualifiedHoursDuration = qualifiedHours ? duration({ hours: qualifiedHours }) : null
  const hours = qualifiedHoursDuration?.hours()
  const minutes = qualifiedHoursDuration?.minutes()

  let qualifiedHoursDisplay: string
  if (hours && hours < 0) {
    qualifiedHoursDisplay = `(no hours)`
  } else if (hours && minutes) {
    qualifiedHoursDisplay = `for ${hours} hours ${minutes} minutes`
  } else if (hours) {
    qualifiedHoursDisplay = `for ${hours} hours`
  } else if (minutes) {
    qualifiedHoursDisplay = `for ${minutes} minutes`
  } else {
    qualifiedHoursDisplay = `(no hours)`
  }

  const lastEditedTime = assessment.authored ? dayjs.utc(assessment.authored) : null

  const assessmentDisplay = [
    `PAT`,
    qualifiedHoursDisplay,
    ...(lastEditedTime ? [`updated on ${lastEditedTime?.format('MMM D, YYYY')}`] : [])
  ].join(' ')

  return assessmentDisplay
}

export const validateNotEmptyDateRangePicker = (value: string[]): Promise<Error | void> => {
  if (!value || value?.length !== 2 || !value[0] || !value[1]) {
    return Promise.reject(new Error('Please add a valid response'))
  }

  return Promise.resolve()
}

export const validateSameDayAppointmentOverviewSection = (
  value: string[],
  caregiverTimezone: string
): Promise<Error | void> => {
  if (!value) return Promise.resolve()

  const appointmentTimeStart = dayjs(value[0]).tz(caregiverTimezone)
  const appointmentTimeEnd = dayjs(value[1]).tz(caregiverTimezone)

  const isOnSameDay = appointmentTimeStart.isSame(appointmentTimeEnd, 'day')

  if (isOnSameDay) {
    return Promise.resolve()
  } else {
    return Promise.reject(new Error('Start and End time should be on the same day.'))
  }
}

export const validateSOCDate = (
  value: string | Date,
  appointmentTimeStart: string | undefined,
  timezone: string
): Promise<Error | void> => {
  if (!value) {
    return Promise.resolve()
  }

  if (!appointmentTimeStart) {
    return Promise.reject(new Error(`First select an Appointment Time`))
  }

  const appointmentTimeStartDayjs = dayjs(appointmentTimeStart).tz(timezone)
  const socDateDayjs = dayjs(value).tz(timezone)

  const isValidSocDate =
    socDateDayjs.isSame(appointmentTimeStartDayjs, 'day') ||
    socDateDayjs.isAfter(appointmentTimeStartDayjs, 'day')

  if (isValidSocDate) {
    return Promise.resolve()
  }
  return Promise.reject(
    new Error(
      `Start of Care Appointment date has to be the same day, or after the Appointment date`
    )
  )
}

interface ShouldFinalizeAssessmentParams {
  assessmentType: AssessmentType
  authorizationSource: ServiceRequest | Consent | undefined
  patientServiceTypes?: ServiceTypeName[]
}

const ASSESSMENTS_SUPPORTING_PRIOR_AUTHORIZATION_REQUEST = [
  AssessmentType.SOC,
  AssessmentType.FUA,
  AssessmentType.SERVICE_PLAN
]

/**
 * Reads the selected HealthcareService name from a Prior Authorization Request ServiceRequest
 * (same extension as extractServiceTypesFromAuthorization).
 */
export function getPriorAuthRequestPrimaryServiceTypeName(
  serviceRequest: ServiceRequest,
  healthcareServices: HealthcareService[]
): string | undefined {
  const serviceTypeRef = serviceRequest.extension?.find(
    (ext) => ext.url === SERVICE_REQUEST_SERVICE_TYPE_EXTENSION
  )?.valueReference?.reference

  if (!serviceTypeRef) return undefined

  const match = healthcareServices.find(
    (serviceType) => `HealthcareService/${serviceType.id}` === serviceTypeRef
  )
  return match?.name ?? undefined
}

export const getShouldSubmitForPendingApproval = ({
  assessmentType,
  authorizationSource,
  patientServiceTypes
}: ShouldFinalizeAssessmentParams): boolean => {
  if (!ASSESSMENTS_SUPPORTING_PRIOR_AUTHORIZATION_REQUEST.includes(assessmentType)) return false

  const authorizationSourceIsPriorAuthorizationRequest =
    authorizationSource?.resourceType === 'ServiceRequest' &&
    authorizationSource.code?.coding?.some((code) => code.code === LOINC_CODE_PRIOR_AUTH_REQUEST)

  if (authorizationSourceIsPriorAuthorizationRequest) {
    // For SOC and Recert assessments with authorization requests, check if the "start care without authorization" feature flag is enabled
    const isSOCOrRecert =
      assessmentType === AssessmentType.SOC || assessmentType === AssessmentType.RECERT

    if (isSOCOrRecert && patientServiceTypes) {
      const startCareWithoutAuthEnabled = checkFeatureConfigByServiceTypes(
        CustomFeatureConfigKey.StartCareWithoutAuthorization,
        patientServiceTypes
      )

      // If feature flag is enabled, return false (finalize as completed instead of pending approval)
      if (startCareWithoutAuthEnabled) {
        return false
      }
    }

    // Default behavior: authorization requests go to pending approval
    return true
  }

  return false
}

export const getShouldFinalizeAssessment = ({
  assessmentType,
  authorizationSource,
  patientServiceTypes
}: ShouldFinalizeAssessmentParams): boolean => {
  const shouldSubmitForPendingApproval = getShouldSubmitForPendingApproval({
    assessmentType,
    authorizationSource,
    patientServiceTypes
  })

  return !shouldSubmitForPendingApproval
}

export const isPractitionerRegisteredNurse = (practitioner: Practitioner): boolean => {
  return Boolean(
    practitioner.qualification?.some((qualification) =>
      qualification.code?.coding?.some(
        (coding) =>
          coding.code === LICENSE_OPTION_CODES.RegisteredNurse &&
          coding.system === HL7_TERMINOLOGY_CODES_LICENSES
      )
    )
  )
}

export const notifyUsersForVisitGenerationError = (error: Error, notify: NotifyType): void => {
  const errorMessage = getErrorMessage(error)
  if (errorMessage === AssessmentGenerationError.MISSING_SOC_DATE_ERROR) {
    notify.error({
      message: 'Missing SOC Appointment Date',
      description: 'Please enter a Start of Care Appointment Date and try again.'
    })
  } else if (errorMessage === AssessmentGenerationError.NOT_ENOUGH_AVAILABILITY_ERROR) {
    notify.error({
      message: 'Unable to generate visits',
      description:
        'Not enough availability to schedule patient visit. Please review the patient schedule and try again.'
    })
  } else {
    notify.error({
      message: 'Unable to generate visits',
      description:
        'Please refresh the page and try again. If the issue persists, contact the support team for assistance.'
    })
  }
}

export const extractPeriodStartAndEndTimesWithTimezone = (
  flatAnswerArray: QuestionnaireResponseItem[],
  startTimeLinkId: AllQuestionnaireItemType,
  endTimeLinkId: AllQuestionnaireItemType,
  caregiverTimezone: string | undefined
): [Dayjs | undefined, Dayjs | undefined] => {
  const periodStart = getAnswerValuesFromItems(flatAnswerArray, startTimeLinkId)?.at(0)
  const periodEnd = getAnswerValuesFromItems(flatAnswerArray, endTimeLinkId)?.at(0)
  const startConverted = periodStart
    ? caregiverTimezone
      ? dayjs.utc(periodStart).tz(caregiverTimezone)
      : dayjs.utc(periodStart)
    : undefined
  const endConverted = periodEnd
    ? caregiverTimezone
      ? dayjs.utc(periodEnd).tz(caregiverTimezone)
      : dayjs.utc(periodEnd)
    : undefined
  return [startConverted, endConverted]
}

// TODO(ENG-5815): Update to receive the linkId as a parameter instead
// of using the shared care questionnaire item constants when updating the
// uncontrolled assessments.
export const getAppointmentTimeWithTimezone = (
  flatAnswerArray: QuestionnaireResponseItem[],
  caregiverTimezone: string | undefined
): [Dayjs | undefined, Dayjs | undefined] => {
  const appointmentStartTime =
    getAnswerValuesFromItems(
      flatAnswerArray,
      SharedCareQuestionnaireItem('ASSESSMENT_OVERVIEW_APPOINTMENT_TIME_START')
    )?.at(0) || undefined
  const appointmentEndTime =
    getAnswerValuesFromItems(
      flatAnswerArray,
      SharedCareQuestionnaireItem('ASSESSMENT_OVERVIEW_APPOINTMENT_TIME_END')
    )?.at(0) || undefined

  const startConverted = appointmentStartTime
    ? caregiverTimezone
      ? dayjs.utc(appointmentStartTime).tz(caregiverTimezone)
      : dayjs.utc(appointmentStartTime)
    : undefined

  const endConverted = appointmentEndTime
    ? caregiverTimezone
      ? dayjs.utc(appointmentEndTime).tz(caregiverTimezone)
      : dayjs.utc(appointmentEndTime)
    : undefined

  return [startConverted, endConverted]
}
/**
 * Returns the options for a questionnaire item
 * @param questionnaireItem - The questionnaire item to get the options for
 * @returns The options for the questionnaire item
 */
export const getSelectOptionsFromQuestionnaireItem = (
  questionnaireItem: QuestionnaireItem
): SelectOption[] => {
  return (
    questionnaireItem.answerOption
      ?.map((option) => ({
        label: option?.valueCoding?.display ?? '',
        value: option?.valueCoding?.code ?? option?.valueCoding?.display ?? ''
      }))
      .filter((option) => option?.label && option?.value) ?? []
  )
}

/**
 * Returns the help text for a questionnaire item
 * @param questionnaireItem - The questionnaire item to get the help text for
 * @returns The help text for the questionnaire item
 */
export const getHelpTextFromQuestionnaireItem = (questionnaireItem: QuestionnaireItem): string => {
  return questionnaireItem.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.display ?? ''
}

/**
 * Checks if a value is a Coding object with a code and display property
 * @param value - The value to check
 * @returns True if the value is a Coding object with a code and display property, false otherwise
 */

export type CodingWithCodeAndDisplay = { code: string; display: string }

export const hasCodeAndDisplay = (value: unknown): value is CodingWithCodeAndDisplay => {
  if (!isCodingObject(value)) return false

  const codeDescriptor = Object.getOwnPropertyDescriptor(value, 'code')
  const displayDescriptor = Object.getOwnPropertyDescriptor(value, 'display')

  const code = codeDescriptor && 'value' in codeDescriptor ? codeDescriptor.value : undefined
  const display =
    displayDescriptor && 'value' in displayDescriptor ? displayDescriptor.value : undefined

  return typeof code === 'string' && typeof display === 'string'
}
